<template>
    <v-row dense id="sales_page">

        <v-col cols="12" lg="3" v-if="search_enabled">

            <v-card class="custom_card mb-2">
                <v-card-text class="pr-6 pl-6">
                    <div>
                        <v-toolbar-title class="field_color">{{$t('general.price_sum')}} : {{price_sum}} ‎€
                        </v-toolbar-title>
                    </div>
                    <div>
                        <v-toolbar-title class="field_color mb-5">{{$t('general.remaining_balance_sum')}}
                            : {{remaining_balance_sum}} ‎€
                        </v-toolbar-title>
                    </div>
                </v-card-text>
            </v-card>

            <v-expansion-panels v-model="search_panel" :disabled="loading">
                <v-expansion-panel>
                    <v-expansion-panel-header>{{$t('general.search')}}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row dense>
                            <v-col cols="12" lg="12" md="4" sm="6">
                                <v-text-field name="search_client" type="text" class="custom_field"
                                              v-model="search_client" :label="$t('clients.singular')" outlined
                                              clearable>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" lg="12" md="4" sm="6">
                                <v-menu v-model="date_from_menu" :close-on-content-click="false" max-width="290">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field name="date_from" type="text" class="custom_field"
                                                      :value="$Helper.methods.formatDate(search.date_from)"
                                                      :label="$t('general.date_from')" outlined readonly
                                                      :disabled="loading" v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="search.date_from" :locale="locale" no-title
                                                   @change="date_from_menu = false">
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" lg="12" md="4" sm="6">
                                <v-menu v-model="date_to_menu" :close-on-content-click="false" max-width="290">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field name="date_to" type="text" class="custom_field"
                                                      :value="$Helper.methods.formatDate(search.date_to)"
                                                      :label="$t('general.date_to')" outlined readonly
                                                      :disabled="loading" v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="search.date_to" :locale="locale" no-title
                                                   @change="date_to_menu = false">
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" lg="12" md="4" sm="6">
                                <v-select name="order_status_id" class="custom_field" v-model="search.order_status_id"
                                          :items="order_statuses" :label="$t('general.order_status')" item-text="name"
                                          item-value="id" outlined clearable :disabled="loading">
                                </v-select>
                            </v-col>
                            <v-col cols="12" align="right">
                                <v-btn color="primary" :disabled="loading" @click="clearSearchForm">
                                    {{$t('general.clear')}}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-col>

        <v-col cols="12" :lg="search_enabled? 9 : 12">
            <!--Datatable Toolbar-->
            <v-toolbar flat color="white" elevation="1" class="custom_datatable_toolbar" height="56">
                <v-toolbar-title>{{$t('sales.list')}}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-if="canCreateItem" color="primary" dark class="custom_button mobile_button"
                       @click="openItemDialog('create', 0)" :title="$t('sales.create')">
                    <v-icon small size="20">add</v-icon>
                </v-btn>

            </v-toolbar>

            <!--Datatable-->
            <ResponsiveDataTable :headers="headers" :items="items" :totalItems="total_items" :loading="loading"
                                 :mobileBreakpoint="960" :isDescending="true" :itemsPerPage="500"
                                 :itemsPerPageOptions="[10, 50, 100, 200, 500]" @options-changed="optionsChanged">
                <!--Buttons-->
                <template v-slot:buttons="{item}">
                    <div class="actions">
                        <v-icon v-if="canViewItem" color="primary" :title="$t('general.view')"
                                @click="openItemDialog('view', item.item.id)">view_list
                        </v-icon>
                        <v-icon v-if="$Helper.methods.canUserDoAction(canUpdateItem, isSuperAdmin, item.item)"
                                color="primary" :title="$t('general.update')"
                                @click="openItemDialog('update', item.item.id)">
                            edit
                        </v-icon>
                        <v-icon v-if="$Helper.methods.canUserDoAction(canDeleteItem, isSuperAdmin, item.item)"
                                color="primary" :title="$t('general.delete')" @click="showDeleteItemDialog(item.item)">
                            delete
                        </v-icon>
                    </div>
                </template>
            </ResponsiveDataTable>
        </v-col>

        <!--Item Dialog-->
        <ManageSaleDialog :show="show_item_dialog" :item_header="item_dialog_header"
                          :is_loading="item_dialog_loading" :item_action="item_action" :item_id="item_id"
                          @close-dialog="closeItemDialog" @submit-dialog="submitItemDialog">
        </ManageSaleDialog>

        <!--Delete item dialog-->
        <StatusDialog :show="show_delete_item_dialog" :header="$t('sales.delete')" color="error"
                      :text="$t('sales.delete_confirmation')" :is_loading="delete_item_dialog_loading"
                      @confirm="deleteItem" @close="closeDeleteItemDialog">
        </StatusDialog>

    </v-row>
</template>

<script>
    import ResponsiveDataTable from '../components/ResponsiveDataTable.vue'
    import ManageSaleDialog from '../components/ManageSaleDialog.vue'
    import StatusDialog from '../components/StatusDialog.vue'
    import _ from 'lodash'

    export default {
        props: {
            search_enabled: {type: Boolean, default: false},
            client_id: {type: Number, default: null},
            criteria: {type: Object, default: null}
        },
        components: {
            ResponsiveDataTable, ManageSaleDialog, StatusDialog
        },
        data: self => ({
            url: 'sales',

            // actions permissions
            canViewItem: false,
            canCreateItem: false,
            canUpdateItem: false,
            canDeleteItem: false,

            price_sum: 0,
            remaining_balance_sum: 0,

            // search
            search_panel: 0,
            date_from_menu: false,
            date_to_menu: false,
            search_client: null,
            search: {
                date_from: null,
                date_to: null
            },
            search_filters: {},
            order_statuses: [],

            // datatable
            headers: [],
            total_items: null,
            items: [],
            loading: false,
            options: {
                page: null,
                itemsPerPage: null,
                sortBy: [],
                sortDesc: [],
                groupBy: [],
                groupDesc: [],
                multiSort: false,
                mustSort: false
            },

            item_id: null,

            // manage item
            show_item_dialog: false,
            item_dialog_header: null,
            item_dialog_loading: false,
            item_action: null,

            // delete item
            show_delete_item_dialog: false,
            delete_item_dialog_loading: false
        }),
        computed: {
            locale() {
                return this.$i18n.locale;
            },
            isSuperAdmin() {
                return this.$store.state.user_details.roles.map(obj => obj.name).indexOf('superadmin') > -1;
            }
        },
        created() {
            this.headers = this.getHeaders();
        },
        async mounted() {
            this.canCreateItem = await this.$store.dispatch('hasPermission', 'create_sale');
            this.canViewItem = await this.$store.dispatch('hasPermission', 'view_sale');
            this.canUpdateItem = await this.$store.dispatch('hasPermission', 'update_sale');
            this.canDeleteItem = await this.$store.dispatch('hasPermission', 'delete_sale');

            // get order statuses
            let order_statuses = await this.$store.dispatch('apiRequest', {
                method: 'get', url: 'settings/order_statuses'
            });
            this.order_statuses = this.$Helper.methods.sortArrayOfObjects({
                array: order_statuses.data, property: 'name'
            });

            // criteria prop not set
            if (!this.criteria) {
                this.setDefaultSearchCriteria();
            }

            // in clients dialog
            if (this.client_id) {
                this.search_filters.client_id = this.client_id;
            }
        },
        watch: {
            locale(val) {
                this.headers = this.getHeaders();
            },
            search: {
                handler(val) {
                    let filters = this.$Helper.methods.createSearchFilters(Object.assign({}, val));

                    // if filters did change get data
                    if (JSON.stringify(this.filters) !== JSON.stringify(filters)) {
                        this.search_filters = filters;

                        setTimeout(() => {
                            this.getData();
                        }, 100);
                    }
                },
                deep: true
            },
            client_id(val) {
                if (val) {
                    this.search_filters.client_id = val;
                    this.getData();
                }
            },
            // change on client search field
            search_client: _.debounce(async function (val) {
                if (val) {
                    if (val.length >= 3) {
                        this.search_filters.client = val;
                        this.getData();
                    }
                } else {
                    delete this.search_filters.client;
                    this.getData();
                }
            }, 500),
            criteria(val) {
                this.items = [];
                this.search_filters = this.$Helper.methods.createSearchFilters(Object.assign({}, val));
                this.getData();
            }
        },
        methods: {
            // return headers array
            getHeaders() {
                return [
                    {text: `${this.$t('sales.date')}`, value: 'sale_date', sortable: true},
                    {text: `${this.$t('clients.singular')}`, value: 'client_name', sortable: true},
                    {text: `${this.$t('general.price')}`, value: 'price', sortable: true},
                    {text: `${this.$t('sales.remaining_balance')}`, value: 'remaining_balance', sortable: true},
                    {text: `${this.$t('datatable.actions')}`, value: 'actions', align: 'right', sortable: false}
                ]
            },

            // search
            // set default values for search form
            setDefaultSearchCriteria() {
                // set default search dates
                let date = new Date();
                this.search.date_to = date.toISOString().substring(0, 10);
                date.setDate(date.getDate() - 30);
                this.search.date_from = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
            },
            // clear search form
            clearSearchForm() {
                this.setDefaultSearchCriteria();
                this.search_client = null;
            },

            // datatable
            // apply changed options on datatable
            async optionsChanged(options) {
                let options_changed = false;
                this.loading = true;

                // check if options changed
                for (let prop in this.options) {
                    if (this.options[prop] !== options[prop]) {
                        options_changed = true;
                        break;
                    }
                }

                if (options_changed) {
                    this.options = Object.assign({}, options);
                    await this.getData();
                }
            },
            // get data from API
            async getData() {

                // search client sales
                if (!this.search_filters.client_id && this.client_id) {
                    this.search_filters.client_id = this.client_id;
                }

                // add search client to filters
                if (!this.search_filters.client && this.search_client) {
                    this.search_filters.client = this.search_client;
                }

                this.loading = true;

                let response = await this.$store.dispatch('apiRequest', {
                    method: 'get', url: this.url, params: {
                        options: this.options, search_filters: this.search_filters
                    }
                });

                this.loading = false;

                // successful response
                if (!response.response) {
                    this.total_items = response.data.total_items;
                    this.items = response.data.items;
                    this.price_sum = response.data.price_sum;
                    this.remaining_balance_sum = response.data.remaining_balance_sum;
                    this.$emit('data-changed', response.data);
                }
            },

            // Manage Item
            openItemDialog(action, id) {
                this.item_dialog_header = (action === 'create') ? this.$t('sales.create')
                    : (action === 'update') ? this.$t('sales.update') : this.$t('sales.view');
                this.item_id = id;
                this.item_action = action;
                this.show_item_dialog = true;
            },
            // clear item dialog related data
            clearItemDialog() {
                this.item_dialog_header = null;
                this.item_id = null;
                this.item_action = null;
            },
            // close item dialog
            closeItemDialog() {
                this.show_item_dialog = false;
                this.clearItemDialog();
            },

            // submit item dialog
            submitItemDialog() {
                this.show_item_dialog = false;
                this.clearItemDialog();
                this.getData();
            },

            // Delete item
            // show delete item dialog
            showDeleteItemDialog(item) {
                this.show_delete_item_dialog = true;
                this.item_id = item.id;
            },
            // close delete item dialog
            closeDeleteItemDialog() {
                this.show_delete_item_dialog = false;
                this.item_id = null;
            },
            // delete item
            async deleteItem() {
                this.delete_item_dialog_loading = true;

                let response = await this.$store.dispatch('apiRequest', {
                    method: 'delete', url: `${this.url}/${this.item_id}`
                });

                this.delete_item_dialog_loading = false;

                // successful response
                if (!response.response) {

                    // close dialog
                    this.closeDeleteItemDialog();

                    // get items
                    await this.getData();
                }
            }
        }
    }
</script>